import { type ChangeEvent } from 'react'
import { Controller, useWatch } from 'react-hook-form'
import { IzzyAnalyzeFail } from '@/components/izzy/analyze-fail'
import { IzzyAnalyzeSuccess } from '@/components/izzy/analyze-success'
import { IzzyChecking } from '@/components/izzy/checking'
import { IzzyGreeting } from '@/components/izzy/greetings'
import { Show } from '@/components/show'
import { ResultTable } from '@/components/tables/result-table'
import { EDUCATION_LEVEL } from '@/constants/education-level'
import { EDUCATION_TYPE } from '@/constants/education-type'
import type { FileUploadStatus } from '@/types/file-upload/file-upload-status'
import type { StudentRequestedCoursesFormMethods } from '@/types/forms/student-requested-courses-form-methods'
import type { Request } from '@/types/requests/request'
import { InputFile } from '../../inputs/file'
import { SelectInput } from '../../inputs/select'
import { TextInput } from '../../inputs/text-input'

interface StudentRequestsFormProps {
  formMethods: StudentRequestedCoursesFormMethods
  fileStatus?: FileUploadStatus
  request?: Request
  requestLabel: string
  fileWarning?: string
  handleFileChange?: (fiel: File) => void
}

export const StudentRequestsForm = ({
  formMethods,
  request,
  fileWarning,
  fileStatus = 'idle',
  handleFileChange,
  requestLabel
}: StudentRequestsFormProps) => {
  const { control, errors, setValue } = formMethods

  const file = useWatch({ control, name: 'previousEducation.file' })

  const handleInputFile = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (selectedFile && setValue) {
      setValue('previousEducation.file', selectedFile)
      if (handleFileChange) {
        handleFileChange(selectedFile)
      }
    }
  }

  return (
    <>
      <span className="font-bold text-[var(--brand-gray-1)] py-4 mt-6">
        Insira os dados que deseja utilizar para a transferência:
      </span>

      <div className="flex flex-col gap-5">
        <div className="flex gap-5">
          <Controller
            name="previousEducation.previousCourseName"
            control={formMethods.control}
            render={({ field }) => (
              <TextInput
                type="text"
                variant="floating"
                label="Nome do Curso Anterior"
                error={errors?.previousEducation?.previousCourseName}
                {...field}
              />
            )}
          />

          <Controller
            name="previousEducation.previousInstituitionName"
            control={formMethods.control}
            render={({ field }) => (
              <TextInput
                type="text"
                variant="floating"
                label="Nome da Instituição Anterior"
                error={errors?.previousEducation?.previousInstituitionName}
                {...field}
              />
            )}
          />
        </div>

        <div className="flex gap-5">
          <SelectInput
            name="previousEducation.modality"
            control={control}
            options={[EDUCATION_TYPE.EAD, EDUCATION_TYPE.PRESENCIAL]}
            variant="floating"
            label="Modalidade"
            error={errors?.previousEducation?.modality}
          />

          <SelectInput
            name="previousEducation.educationLevel"
            control={control}
            options={[EDUCATION_LEVEL.GRADUACAO]}
            variant="floating"
            label="Nível de Ensino"
            error={errors?.previousEducation?.educationLevel}
          />
        </div>
      </div>

      <div className="mt-10 relative">
        {fileStatus === 'idle' && (
          <IzzyGreeting
            label={
              <div>
                <p>
                  Olá, eu sou o <strong>Izzy!</strong>
                </p>
                <p>Sou seu assistente para te auxiliar com {requestLabel}.</p>
                <p>
                  Estou aqui para te ajudar a ter{' '}
                  <strong>o melhor aproveitamento possível!</strong>
                </p>
                <p>
                  O primeiro passo é preencher os dados sobre seu histórico:
                </p>
              </div>
            }
            className="w-48 right-10 top-[-85px] absolute z-20"
          />
        )}
        {fileStatus === 'pending' && (
          <IzzyChecking className="w-48 right-10 top-[-85px] absolute z-20" />
        )}
        {(fileStatus === 'error' || fileStatus === 'warning') && (
          <IzzyAnalyzeFail
            className="w-48 right-10 top-[-85px] absolute z-20"
            label="O documento anexado não foi considerado válido."
          />
        )}
        {fileStatus === 'success' && (
          <IzzyAnalyzeSuccess
            className="w-48 right-10 top-[-85px] absolute z-20"
            label="Tudo certo com o documento, Já puxei algumas informações aqui, podemos continuar!"
          />
        )}

        <span className="font-bold text-[var(--brand-gray-1)]">
          Anexe seu “Histórico Escolar” referente aos dados acima:
        </span>

        <InputFile
          fileName={file?.name}
          onChange={handleInputFile}
          variant="floating"
          label="Histórico Escolar"
          error={errors?.previousEducation?.file}
          status={fileStatus}
          warning={fileWarning}
          accept=".png,.jpg,.jpeg,.pdf"
        />
        <Show when={!!request?.id && fileStatus === 'success'}>
          <div className="border-b border-muted-foreground/40 w-full mt-10"></div>
          <div className="mt-10">
            <ResultTable request={request} />
          </div>
        </Show>
      </div>
    </>
  )
}
